/**
 * The empty shell behind the blocking subscribe dialog (TMMAPP-0116; the v2 shell, TMMAPP-0150 U2):
 * the sidebar's wordmark and nav labels — no Today block, no counts — and the top bar's
 * avatar over an empty canvas. It renders on whatever route was opened, and requests no owner data
 * (a NONE person would only get refusals). Inert: the dialog is the page's only content. `checking`
 * is the moment `/me/billing` is deciding; the shell shows and the dialog waits.
 */
import { Avatar, Box, Drawer, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { SubscribeDialog } from '@/features/subscribe/SubscribeDialog';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme';
import { initialOf } from './initials';
import { SidebarColumn, SidebarNavList, SidebarWordmark } from './Sidebar';
import { sidebarPaperSx } from './sidebarPaper';
import { FULL_HEIGHT_SX } from './barHeight';
import { MAIN_ID, SkipLink } from './SkipLink';

const noop = () => undefined;

export function GatedShell({ blocking }: { blocking: boolean }) {
  const me = useMe();
  const theme = useTheme();
  const tokens = resolveTokens(theme);
  const wide = useMediaQuery(theme.breakpoints.up('lg'));
  const { pathname } = useLocation();

  return (
    <>
      <Box inert data-gated-shell="" sx={{ display: 'flex', ...FULL_HEIGHT_SX }}>
        <SkipLink />
        {wide && (
          <Drawer
            variant="permanent"
            sx={(theme) => ({
              flexShrink: 0,
              ...sidebarPaperSx(theme),
              '& .MuiDrawer-paper': { ...sidebarPaperSx(theme), borderInlineEnd: 'none' },
            })}
          >
            <SidebarColumn>
              <SidebarWordmark onNavigate={noop} />
              <SidebarNavList
                subscriber={false}
                pathname={pathname}
                onNavigate={noop}
                counts={false}
                bare
              />
            </SidebarColumn>
          </Drawer>
        )}
        <Box sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <Box
            component="header"
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              px: { xs: 2, sm: 4 },
              py: '14px',
              bgcolor: tokens.bg,
              borderBottom: '1px solid',
              borderColor: tokens.line,
            }}
          >
            <Avatar
              aria-hidden
              sx={{
                width: 32,
                height: 32,
                bgcolor: tokens.brand,
                color: tokens.brandInk,
                fontSize: 12,
                fontWeight: 600,
              }}
            >
              {initialOf(me.user.displayName)}
            </Avatar>
          </Box>
          <Box
            component="main"
            id={MAIN_ID}
            tabIndex={-1}
            sx={{ flexGrow: 1, minWidth: 0, bgcolor: 'background.default' }}
          />
        </Box>
      </Box>
      <SubscribeDialog open={blocking} mode="blocking" onClose={noop} />
    </>
  );
}
